// Respaldo/recuperación de un borrador en curso, reutilizable por cualquier pantalla
// (mismo patrón del carrito POS y de la Recepción de inventario).
//
// localStorage al instante (mismo equipo+usuario) + borrador en el servidor cada 30s
// (por si el cierre del navegador fue en otro equipo). Un borrador por
// (empresa, usuario, clave) — el backend saca empresa/usuario del JWT (RPCs genéricas
// en el esquema por defecto, script 634): borrador_pantalla_guardar/obtener.
import { useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api/client';
import { useAuth } from './useAuth';

interface UseBorradorPantallaOpts<T> {
  /** Clave lógica de la pantalla, p.ej. 'inv_ajuste' | 'cont_asiento'. */
  clave: string;
  /** Si es false, el hook no restaura ni autoguarda (p.ej. modo edición). */
  activo: boolean;
  /** Estado serializable actual (snapshot) que se respalda. */
  snapshot: T;
  /** true si el snapshot actual NO está vacío (hay algo que perder). */
  hayDatos: boolean;
  /** Aplica un snapshot restaurado a los setters del formulario. */
  onRestaurar: (data: T) => void;
}

export function useBorradorPantalla<T>(opts: UseBorradorPantallaOpts<T>): { limpiar: () => void } {
  const { clave, activo, snapshot, hayDatos, onRestaurar } = opts;
  const { user } = useAuth();
  const empresaId = user?.empresa?.id;
  const usuarioId = user?.id;
  const draftKey = `borrador_${clave}_${empresaId ?? 'na'}_${usuarioId ?? 'na'}`;

  const restauradoRef = useRef(false);
  const dirtyRef = useRef(false);
  const snapshotRef = useRef(snapshot); snapshotRef.current = snapshot;
  const hayDatosRef = useRef(hayDatos); hayDatosRef.current = hayDatos;
  const onRestaurarRef = useRef(onRestaurar); onRestaurarRef.current = onRestaurar;

  // Restaurar (una sola vez, cuando ya sabemos empresa/usuario y la pantalla está
  // activa): localStorage primero (instantáneo), luego el borrador del servidor
  // (por si fue desde otro equipo). Solo si el estado actual está vacío, para no
  // pisar lo que el usuario ya empezó a escribir.
  useEffect(() => {
    if (!activo) return;
    if (restauradoRef.current) return;
    if (!empresaId) return; // espera a que cargue la sesión (la clave depende de ella)
    if (hayDatosRef.current) return;
    restauradoRef.current = true;

    const aplicar = (data: T | null | undefined) => {
      if (data == null) return;
      if (hayDatosRef.current) return; // por si algo cambió entre el fetch y la respuesta
      onRestaurarRef.current(data);
    };

    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) { aplicar(JSON.parse(raw) as T); return; }
    } catch { /* respaldo local ilegible: se ignora */ }

    apiClient.post<T | null>('/rpc/borrador_pantalla_obtener', { _clave: clave })
      .then((res) => aplicar(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, empresaId, draftKey, clave]);

  // Autoguardado local al instante en cada cambio del snapshot.
  useEffect(() => {
    if (!activo) return;
    if (!restauradoRef.current) return;
    dirtyRef.current = true;
    try {
      if (hayDatos) localStorage.setItem(draftKey, JSON.stringify(snapshot));
      else localStorage.removeItem(draftKey);
    } catch { /* cuota llena: el respaldo local es best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, hayDatos, draftKey, activo]);

  // Cada 30s, si hay cambios sin respaldar en el servidor, los guarda.
  useEffect(() => {
    if (!activo) return;
    const id = setInterval(() => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      const payload = hayDatosRef.current ? snapshotRef.current : null;
      apiClient.post('/rpc/borrador_pantalla_guardar', { _clave: clave, _payload: payload })
        .catch(() => { dirtyRef.current = true; });
    }, 30000);
    return () => clearInterval(id);
  }, [activo, clave]);

  // Al confirmar/guardar: limpiar el respaldo YA (local y servidor), sin esperar el ciclo.
  const limpiar = () => {
    try { localStorage.removeItem(draftKey); } catch { /* best-effort */ }
    dirtyRef.current = false;
    void apiClient.post('/rpc/borrador_pantalla_guardar', { _clave: clave, _payload: null }).catch(() => {});
  };

  return { limpiar };
}
